import { useEffect, useRef } from "react";

/** The fixed site header. The sticky category rail parks directly under it,
 *  so this is also where the top of the list wants to land. */
const HEADER_HEIGHT = 68;

/** How long to wait before checking the scroll landed where it was aimed. */
const SETTLE_MS = 450;

/**
 * Keeps a results list in view when the guest changes page, category, city,
 * sort or a filter.
 *
 * The default was a jump to the very top of the document: click page 5 from
 * the pagination at the foot of the grid and you are looking at the hero
 * again, four screens above the results you just asked for. So every link and
 * navigation that drives one of these lists passes `resetScroll: false`, and
 * this puts the top of the list under the header instead.
 *
 * Two deliberate limits:
 *
 *  - **It only scrolls up.** A guest who switches category while still reading
 *    the intro is left where they are, not dragged down into the grid.
 *  - **It never fires on first paint**, so a shared or crawled URL renders at
 *    the top of the page like any other.
 *
 * `lastKey` is module state rather than a ref because a change of route param
 * (category, city) can remount the page, which would take a ref's history with
 * it and leave the guest stranded mid-list. It is only ever read or written
 * inside the effect, so the server never touches it.
 */
let lastKey: string | null = null;

export function useResultsAnchor(key: string) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /* Claimed only once the scroll below has run, never up front: the page can
       remount mid-navigation (the grid suspends and the skeleton takes its
       place), and a key claimed by the run that got torn down would make the
       run that replaces it think there was nothing left to do. */
    if (lastKey === null) {
      lastKey = key;
      return;
    }
    if (lastKey === key) return;

    /** Aim at the top of the list. Returns nothing; it is safe to repeat. */
    const aim = () => {
      const el = ref.current;
      if (!el) return;
      /* Clamped at 0: on a short page the anchor can sit within the header's
         height of the document top, and an unclamped negative target turns
         "leave them where they are" into a scroll to the top. */
      const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - HEADER_HEIGHT);
      if (window.scrollY <= top) return;
      /* `instant`, explicitly — not `auto`, which defers to the stylesheet,
         and the stylesheet sets `scroll-behavior: smooth` on <html>.
         A smooth scroll is an animation, and this animation is cancelled by
         the browser's own scroll anchoring the moment the grid below grows
         into its images, which is exactly when this runs. The guest then sees
         the page not move at all. */
      window.scrollTo({ top, behavior: "instant" });
    };

    /**
     * Aimed twice, because the first shot is fired at a page that has not
     * finished growing.
     *
     * The effect runs the moment the new results commit, while the cards are
     * still sizing to their images. That growth moves the target, and the
     * browser's own scroll anchoring adjusts the position to compensate —
     * which silently cancels a smooth scroll in flight, so the guest sees the
     * page simply not move. The second pass, once the layout has settled,
     * finishes the job; if the first one landed, it finds nothing to do and
     * leaves the guest alone.
     */
    const first = setTimeout(() => {
      lastKey = key;
      aim();
    }, 0);
    const second = setTimeout(aim, SETTLE_MS);

    return () => {
      clearTimeout(first);
      clearTimeout(second);
    };
  }, [key]);

  return ref;
}
